import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { useMerchantPayoutFlags } from '../../hooks/useMerchantPayoutFlags';
import LogoutModal from '../../components/LogoutModal';
import WalletCreditsModal from '../../components/WalletCreditsModal';
import SuccessModal from '../../components/SuccessModal';
import TransactionStatusModal from '../../components/TransactionStatusModal';
import TransactionFeesModal from '../../components/TransactionFeesModal';
import TpinModal from '../../components/TpinModal';
import Footer from '../../components/Footer';

interface PayoutFormData {
  name: string;
  mobile: string;
  accountNumber: string;
  email: string;
  amount: string;
  ifscCode: string;
  bankName: string;
  branchAddress: string;
}

const PAYOUT_BLACKLIST_MESSAGE = 'Your Account is Blacklisted, Please Contact Support';

function fraudBlacklistLiveFallback(data: { message?: string; code?: string } | undefined): string {
  if (data?.message) return data.message;
  return data?.code === 'BLACKLISTED_IFSC_CODE'
    ? 'This IFSC code is blacklisted.'
    : 'This payout details are blacklisted.';
}

function fraudBlacklistSubmitFallback(data: { message?: string; code?: string } | undefined): string {
  if (data?.message) return data.message;
  return data?.code === 'BLACKLISTED_IFSC_CODE'
    ? 'This IFSC code is blacklisted. Please change beneficiary details.'
    : 'This payout details are blacklisted. Please change beneficiary details.';
}

const MerchantNewPayouts: React.FC = () => {
  const { user, logout, refreshProfile } = useAuth();
  const { viyonaEnabled, poolpayEnabled } = useMerchantPayoutFlags();
  const [activeTab, setActiveTab] = useState('create-payout');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showTransactionFeesModal, setShowTransactionFeesModal] = useState(false);
  const [showTpinModal, setShowTpinModal] = useState(false);
  const [tpinError, setTpinError] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [errorModalVariant, setErrorModalVariant] = useState<'default' | 'warning'>('default');
  const [transactionFeesData, setTransactionFeesData] = useState<any>(null);
  const [formData, setFormData] = useState<PayoutFormData>({
    name: '',
    mobile: '',
    accountNumber: '',
    email: '',
    amount: '',
    ifscCode: '',
    bankName: '',
    branchAddress: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<PayoutFormData>>({});
  const [ifscLoading, setIfscLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [fraudCheckLoading, setFraudCheckLoading] = useState(false);
  const [fraudBlocked, setFraudBlocked] = useState(false);

  const merchantPayoutBlacklisted = Boolean(user?.payout_blacklisted);

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);
  
  // Status check states
  const [statusCheckRef, setStatusCheckRef] = useState('');
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusResult, setStatusResult] = useState<any>(null);

  const merchantSidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠' },
    { id: 'account', label: 'My Account', icon: '👤' },
    { id: 'payouts', label: 'Payouts', icon: '💰' },
    { id: 'reports', label: 'Reports', icon: '📊' },
    ...(viyonaEnabled ? [{ id: 'create-payout' as const, label: 'Create Payout (v1 Viyona)', icon: '➕' }] : []),
    ...(poolpayEnabled ? [{ id: 'create-payout-v2' as const, label: 'Create Payout (v2 PoolPay)', icon: '💠' }] : []),
    { id: 'logout', label: 'Logout', icon: '🚪' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch {
      /* ignore */
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Redirect if Viyona payout is disabled by manager
  useEffect(() => {
    if (!viyonaEnabled) {
      window.location.href = '/merchant';
    }
  }, [viyonaEnabled]);

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'home') {
      window.location.href = '/merchant';
    } else if (tabId === 'account') {
      window.location.href = '/merchant/account';
    } else if (tabId === 'payouts') {
      window.location.href = '/merchant/payouts'; // Sidebar goes to payouts list
    } else if (tabId === 'reports') {
      window.location.href = '/merchant/reports';
    } else if (tabId === 'create-payout') {
      window.location.href = '/merchant/new-payouts';
    } else if (tabId === 'create-payout-v2') {
      window.location.href = '/merchant/new-payouts/poolpay';
    } else if (tabId === 'logout') {
      setShowLogoutModal(true);
    }
    // If already on new-payouts, don't navigate
    // Close sidebar on mobile after navigation
    setSidebarOpen(false);
  };

  const handleCreditMoney = () => {
    setShowCreditsModal(true);
  };

  const handleNewPayout = () => {
    // Already on new payouts page, but could refresh or reset form
    setFormData({
      name: '',
      mobile: '',
      accountNumber: '',
      email: '',
      amount: '',
      ifscCode: '',
      bankName: '',
      branchAddress: ''
    });
    setErrors({});
  };

  const handleMyProfile = () => {
    window.location.href = '/merchant/account';
  };

  const handleInputChange = (field: keyof PayoutFormData, value: string) => {
    // Special handling for name field: only allow alphabets and spaces, max 25 characters
    if (field === 'name') {
      // Remove any special characters (keep only alphabets and spaces)
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      // Limit to 25 characters
      const limitedValue = filteredValue.slice(0, 25);
      setFormData(prev => ({
        ...prev,
        [field]: limitedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Auto-fill bank name when IFSC code is entered
    if (field === 'ifscCode') {
      const ifscCode = value.toUpperCase().trim();
      if (ifscCode.length === 11) {
        validateAndFetchBankName(ifscCode);
      } else {
        setFormData(prev => ({ ...prev, bankName: '', branchAddress: '' }));
      }
    }
  };

  // IFSC validation and bank name auto-fill
  const validateAndFetchBankName = async (ifscCode: string) => {
    if (!ifscCode.trim()) {
      setErrors(prev => ({ ...prev, ifscCode: '' }));
      return;
    }

    // Basic IFSC format validation
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      setErrors(prev => ({ ...prev, ifscCode: 'Invalid IFSC code format' }));
      return;
    }

    setIfscLoading(true);
    try {
      const response = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
      
      if (response.ok) {
        const bankData = await response.json();
        if (bankData.BANK && bankData.BANK !== '') {
          const branchAddress = [bankData.BRANCH, bankData.ADDRESS].filter(Boolean).join(', ');
          setFormData(prev => ({ ...prev, bankName: bankData.BANK, branchAddress }));
          setErrors(prev => ({ ...prev, ifscCode: '', bankName: '' }));
        } else {
          setFormData(prev => ({ ...prev, bankName: '', branchAddress: '' }));
          setErrors(prev => ({ ...prev, ifscCode: 'Bank not found for this IFSC code' }));
        }
      } else {
        setFormData(prev => ({ ...prev, bankName: '', branchAddress: '' }));
        setErrors(prev => ({ ...prev, ifscCode: 'Invalid IFSC code or bank not found' }));
      }
    } catch {
      setFormData(prev => ({ ...prev, bankName: '', branchAddress: '' }));
      setErrors(prev => ({ ...prev, ifscCode: 'Failed to validate IFSC code' }));
    } finally {
      setIfscLoading(false);
    }
  };

  const normalizeIfsc = (v: string) => (v || '').toUpperCase().replace(/\s+/g, '').trim();
  const normalizeAccountNumber = (v: string) => (v || '').replace(/\s+/g, '').trim();

  // Live fraud check while typing (account + IFSC pair; bank name not used for blacklist)
  useEffect(() => {
    if (merchantPayoutBlacklisted) {
      setFraudBlocked(false);
      return;
    }
    const accountNumber = normalizeAccountNumber(formData.accountNumber);
    const ifsc = normalizeIfsc(formData.ifscCode);

    const isReady =
      /^\d{6,20}$/.test(accountNumber) && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);

    if (!isReady) {
      setFraudBlocked(false);
      return;
    }

    const t = window.setTimeout(async () => {
      setFraudCheckLoading(true);
      try {
        const res = await api.post('/merchants/fraud/check-payout-details', {
          account_number: accountNumber,
          ifsc_code: ifsc,
        });

        const blocked = Boolean(res.data?.blocked);
        setFraudBlocked(blocked);
        if (blocked) {
          setModalMessage(fraudBlacklistLiveFallback(res.data));
          setErrorModalVariant('warning');
          setShowErrorModal(true);
        }
      } catch {
        // ignore live-check failures (backend will still enforce on submit)
      } finally {
        setFraudCheckLoading(false);
      }
    }, 500);

    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.accountNumber, formData.ifscCode, merchantPayoutBlacklisted]);

  const validateForm = (): boolean => {
    const newErrors: Partial<PayoutFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 25) {
      newErrors.name = 'Name must be maximum 25 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain alphabets and spaces';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.trim())) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    } else if (!/^\d{9,18}$/.test(formData.accountNumber.trim())) {
      newErrors.accountNumber = 'Please enter a valid account number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.amount.trim())) {
      newErrors.amount = 'Please enter a valid amount';
    } else {
      const amountValue = parseFloat(formData.amount);
      if (amountValue < 500) {
        newErrors.amount = 'Minimum transfer amount is ₹500';
      }
    }

    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode.trim().toUpperCase())) {
      newErrors.ifscCode = 'Please enter a valid IFSC code';
    }

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const runFraudCheckBeforeSubmit = async (): Promise<{ blocked: boolean; message?: string; code?: string }> => {
    const accountNumber = normalizeAccountNumber(formData.accountNumber);
    const ifsc = normalizeIfsc(formData.ifscCode);

    try {
      setFraudCheckLoading(true);
      const res = await api.post('/merchants/fraud/check-payout-details', {
        account_number: accountNumber,
        ifsc_code: ifsc,
      });
      const blocked = Boolean(res.data?.blocked);
      return { blocked, message: res.data?.message, code: res.data?.code };
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message;
      if (status === 403 || (message && /blacklist/i.test(message))) {
        return { blocked: true, message, code: error?.response?.data?.code };
      }
      return { blocked: false };
    } finally {
      setFraudCheckLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (merchantPayoutBlacklisted) {
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    // Force a fresh fraud check on submit before any further action.
    const fraudResult = await runFraudCheckBeforeSubmit();
    setFraudBlocked(Boolean(fraudResult.blocked));
    if (fraudResult.blocked || fraudBlocked) {
      setModalMessage(fraudBlacklistSubmitFallback(fraudResult));
      setErrorModalVariant('warning');
      setShowErrorModal(true);
      return;
    }

    // Start the 3-second countdown with animation
    setIsSubmitting(true);
    setCountdown(3);
    
    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // After 3 seconds, proceed with submission
    setTimeout(async () => {
      try {
        // First, get transaction fees information (Viyona v1)
        const feesResponse = await api.get(`/merchants/transaction-fees?amount=${formData.amount}`);
        const feesData = feesResponse.data.data;
        
        // Store fees data and show beautiful modal
        setTransactionFeesData(feesData);
        setShowTransactionFeesModal(true);
        setIsSubmitting(false);
        return;
        
      } catch (error: any) {
        const errorMsg = error.response?.data?.message || 'Error submitting payout. Please try again.';
        setModalMessage(errorMsg);
        setErrorModalVariant('default');
        setShowErrorModal(true);
        setIsSubmitting(false);
      }
    }, 3000);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    // Optionally redirect to payouts list or reset form
    window.location.href = '/merchant/payouts';
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setErrorModalVariant('default');
  };

  const handleTransactionFeesConfirm = async () => {
    setShowTransactionFeesModal(false);
    setLoading(true);
    setTpinError('');

    try {
      const tpinRes = await api.post('/merchants/send-tpin');
      if (!tpinRes.data.success) {
        setModalMessage(tpinRes.data.message || 'Failed to send TPIN. Please ensure your mobile number is updated in your profile.');
        setErrorModalVariant('default');
        setShowErrorModal(true);
        setLoading(false);
        return;
      }
      setShowTpinModal(true);
    } catch (err: any) {
      setModalMessage(err.response?.data?.message || 'Failed to send TPIN. Please try again.');
      setErrorModalVariant('default');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTpinSubmit = async (tpin: string) => {
    setTpinError('');
    setLoading(true);

    try {
      const response = await api.post('/merchants/new-payouts', {
        merchant_reference_number: `PAYOUT_${Date.now()}`,
        account_name: formData.name,
        amount: parseFloat(formData.amount),
        account_number: formData.accountNumber,
        ifsc_code: formData.ifscCode.toUpperCase(),
        bank_name: formData.bankName,
        transfer_type: 'IMPS',
        customer_email: formData.email,
        customer_phone: formData.mobile,
        remark: 'Payout Merchant',
        tpin
      });

      setShowTpinModal(false);

      setFormData({
        name: '',
        mobile: '',
        accountNumber: '',
        email: '',
        amount: '',
        ifscCode: '',
        bankName: '',
        branchAddress: ''
      });

      const successMsg = response.data.message || 'Payout request submitted successfully!';
      const transactionId = response.data.data?.transaction_id || response.data.data?.id;
      const fullMessage = transactionId
        ? `${successMsg}\n\nTransaction ID: ${transactionId}`
        : successMsg;

      setModalMessage(fullMessage);
      setShowSuccessModal(true);
    } catch (error: any) {
      const res = error.response?.data;
      let errMsg = res?.message || 'Error submitting payout. Please try again.';
      if (res?.error) errMsg += ` (${res.error})`;
      if (res?.trace_id) errMsg += ` [Ref: ${res.trace_id}]`;
      if (res?.data && typeof res.data === 'object' && (res.data.message || res.data.error)) {
        errMsg += ` — ${res.data.message || res.data.error}`;
      } else if (res?.data && typeof res.data === 'string') {
        errMsg += ` — ${res.data}`;
      }
      setTpinError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleTransactionFeesCancel = () => {
    setShowTransactionFeesModal(false);
    setTransactionFeesData(null);
  };

  const handleStatusCheck = async () => {
    if (!statusCheckRef.trim()) {
      setModalMessage('Please enter a merchant reference number or transaction ID');
      setErrorModalVariant('default');
      setShowErrorModal(true);
      return;
    }

    setStatusCheckLoading(true);
    
    try {
      // Use merchant endpoint which uses merchant-specific API keys
      const response = await api.get(`/merchants/payout-status/${statusCheckRef.trim()}`);
      setStatusResult(response.data);
      setShowStatusModal(true);
      setStatusCheckRef(''); // Clear input after successful check
      
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Error checking transaction status. Please try again.';
      setModalMessage(errorMsg);
      setErrorModalVariant('default');
      setShowErrorModal(true);
    } finally {
      setStatusCheckLoading(false);
    }
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusResult(null);
  };

  // Helper function to parse DD-MM-YYYY format date
  const parseTransactionDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    
    // Check if it's in DD-MM-YYYY format
    const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = dateString.match(ddMMyyyyPattern);
    
    if (match) {
      const [, day, month, year] = match;
      // Create date: month is 0-indexed in JavaScript Date, so subtract 1
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      // Validate the date
      if (date.getFullYear() === parseInt(year) && 
          date.getMonth() === parseInt(month) - 1 && 
          date.getDate() === parseInt(day)) {
        return date;
      }
    }
    
    // Try standard Date parsing for other formats
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    
    return null;
  };

  // Helper function to format date for display
  const formatDate = (dateString: string | Date | null, includeTime: boolean = true): string => {
    if (!dateString) return 'N/A';
    
    let date: Date | null = null;
    let isDateOnly = false;
    
    if (dateString instanceof Date) {
      date = dateString;
    } else if (typeof dateString === 'string') {
      // Check if it's in DD-MM-YYYY format (date only, no time)
      const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      if (ddMMyyyyPattern.test(dateString)) {
        isDateOnly = true;
      }
      date = parseTransactionDate(dateString);
    }
    
    if (!date || isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    // If it's date-only format, don't show time
    if (isDateOnly || !includeTime) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    
    // Otherwise, show date with time
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatStatusMessage = (result: any): string => {
    if (!result || !result.data) {
      return 'No status information available.';
    }

    const data = result.data;
    let message = `Transaction Status: ${data.status}\n\n`;
    
    if (data.merchant_reference_number) {
      message += `Merchant Reference: ${data.merchant_reference_number}\n`;
    }
    
    if (data.viyona_transaction_id) {
      message += `Transaction ID: ${data.viyona_transaction_id}\n`;
    }
    
    if (data.amount) {
      message += `Amount: ₹${data.amount}\n`;
    }
    
    if (data.bank_reference_number) {
      message += `Bank Reference: ${data.bank_reference_number}\n`;
    }
    
    // Check transaction_date in multiple locations (data.transaction_date or decrypted_response.transaction_date)
    // Note: transaction_date from Viyona Pay API is date-only (DD-MM-YYYY format), not datetime
    const transactionDate = data.transaction_date || 
                           data.decrypted_response?.transaction_date ||
                           data.decrypted_response?.response_body?.transaction_date;
    
    if (transactionDate) {
      // transaction_date from Viyona Pay API is date-only (DD-MM-YYYY), so don't show time
      const formattedDate = formatDate(transactionDate, false);
      message += `Transaction Date: ${formattedDate}\n`;
    }
    
    // Show transaction time from database if available (created_at has full datetime)
    if (data.created_at) {
      message += `Transaction Time: ${formatDate(data.created_at)}\n`;
    }
    
    if (data.error_message) {
      message += `\nError Message: ${data.error_message}`;
    }
    
    if (data.updated_at) {
      message += `\nLast Updated: ${formatDate(data.updated_at)}`;
    }

    return message;
  };

  const inputBase = 'w-full min-w-0 py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 box-border';
  const inputError = 'border-red-500 focus:border-red-500 focus:ring-red-500/20';

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-slate-100 to-slate-200">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside
        id="merchant-sidebar"
        className="fixed top-0 left-0 z-50 h-full w-64 bg-[#0b0d32] text-slate-100 shadow-xl transition-transform duration-200 ease-out"
        style={isDesktop ? { transform: 'translateX(0)' } : sidebarOpen ? { transform: 'translateX(0)' } : { transform: 'translateX(-100%)' }}
      >
        <div className="p-5 border-b border-slate-600">
          <div className="flex items-center justify-center w-full">
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
              alt="YuvanAPI"
              className="h-[3.75rem] w-auto max-w-[min(19rem,100%)] object-contain object-center"
            />
          </div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {merchantSidebarItems.map(item => (
            <button
              key={item.id}
              type="button"
              className={`flex items-center gap-3 w-full py-3 px-4 rounded-lg text-left font-medium transition-colors ${activeTab === item.id ? 'bg-violet-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}
              onClick={() => handleNavigation(item.id)}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="leading-tight">
                {item.id === 'create-payout' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v1 Viyona)</span>
                  </>
                ) : item.id === 'create-payout-v2' ? (
                  <>
                    <span className="block">Create Payout</span>
                    <span className="block text-xs opacity-90">(v2 PoolPay)</span>
                  </>
                ) : (
                  <span className="block">{item.label}</span>
                )}
              </span>
            </button>
          ))}
        </nav>
      </aside>

      <header className="flex items-center gap-3 p-4 bg-[#0b0d32] text-white shadow sticky top-0 z-30" style={isDesktop ? { display: 'none' } : undefined}>
        <div className="flex min-w-0 flex-1 justify-center">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo4.png`}
            alt="YuvanAPI"
            className="h-14 w-auto max-w-[min(17rem,100%)] object-contain object-center"
          />
        </div>
        <button type="button" className="shrink-0 rounded-lg p-2 hover:bg-slate-700" onClick={toggleSidebar} aria-label="Menu">
          ☰
        </button>
      </header>

      <main id="merchant-main" className="flex-1 p-6 pt-8" style={isDesktop ? { marginLeft: '16rem' } : undefined}>
        <div
          className="flex flex-col gap-4 mb-6"
          style={isDesktop ? { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' } : undefined}
        >
          <h1 className="text-2xl font-bold text-slate-800 m-0 shrink-0">Welcome to YuvanAPI Merchant!</h1>
          <div className="flex flex-wrap gap-2" style={{ flexShrink: 0 }}>
            <button type="button" onClick={handleCreditMoney} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-blue-700 whitespace-nowrap shrink-0">💳 CREDIT MONEY</button>
            <button type="button" onClick={handleNewPayout} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 whitespace-nowrap shrink-0">💰 + Payout</button>
            <button type="button" onClick={handleMyProfile} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-slate-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-slate-700 whitespace-nowrap shrink-0">👤 My Profile</button>
            <button type="button" onClick={handleLogoutClick} className="flex items-center gap-2 py-2.5 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-red-700 whitespace-nowrap shrink-0">🚪 Logout</button>
          </div>
        </div>

        <div
          className="grid grid-cols-1 gap-6"
          style={isDesktop ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1.5rem' } : undefined}
        >
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm min-w-0">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl">📋</span>
              <div>
                <h3 className="text-lg font-bold text-slate-800 m-0">Payout Details</h3>
                <p className="text-sm text-slate-600 m-0">Fill in the beneficiary information below</p>
              </div>
            </div>
            {merchantPayoutBlacklisted && (
              <div
                className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
                role="alert"
              >
                <svg
                  className="h-5 w-5 flex-shrink-0 text-amber-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
                <span>{PAYOUT_BLACKLIST_MESSAGE}</span>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div
                className="grid grid-cols-1 gap-4"
                style={isDesktop ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}
              >
                <div className="flex flex-col gap-1">
                  <label htmlFor="name" className="text-sm font-semibold text-slate-700 flex items-center gap-1">👤 Holder&apos;s Name</label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Enter beneficiary's full name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.name ? inputError : ''}`}
                  />
                  {errors.name && <span className="text-red-600 text-sm">{errors.name}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="mobile" className="text-sm font-semibold text-slate-700 flex items-center gap-1">📱 Mobile Number</label>
                  <input
                    type="tel"
                    id="mobile"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.mobile ? inputError : ''}`}
                  />
                  {errors.mobile && <span className="text-red-600 text-sm">{errors.mobile}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="email" className="text-sm font-semibold text-slate-700 flex items-center gap-1">📧 Email Address</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.email ? inputError : ''}`}
                  />
                  {errors.email && <span className="text-red-600 text-sm">{errors.email}</span>}
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="accountNumber" className="text-sm font-semibold text-slate-700 flex items-center gap-1">🔢 Account Number</label>
                  <input
                    type="text"
                    id="accountNumber"
                    placeholder="Enter account number"
                    value={formData.accountNumber}
                    onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.accountNumber ? inputError : ''}`}
                  />
                  {errors.accountNumber && <span className="text-red-600 text-sm">{errors.accountNumber}</span>}
                  {!errors.accountNumber && fraudCheckLoading && (
                    <span className="text-slate-500 text-xs">Checking beneficiary against blacklist...</span>
                  )}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="ifscCode" className="text-sm font-semibold text-slate-700 flex items-center gap-1">🏛️ IFSC Code</label>
                  <div className="relative">
                    <input
                      type="text"
                      id="ifscCode"
                      placeholder="Enter IFSC code (e.g., SBIN0001234)"
                      value={formData.ifscCode}
                      onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
                      className={`${inputBase} border-slate-200 ${errors.ifscCode ? inputError : ''}`}
                      disabled={ifscLoading || merchantPayoutBlacklisted}
                    />
                    {ifscLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-slate-200 border-t-violet-500 rounded-full animate-spin" />
                    )}
                  </div>
                  {errors.ifscCode && <span className="text-red-600 text-sm">{errors.ifscCode}</span>}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="bankName" className="text-sm font-semibold text-slate-700 flex items-center gap-1">🏪 Bank Name</label>
                  <input
                    type="text"
                    id="bankName"
                    placeholder="Enter bank name"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.bankName ? inputError : ''}`}
                  />
                  {errors.bankName && <span className="text-red-600 text-sm">{errors.bankName}</span>}
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="branchAddress" className="text-sm font-semibold text-slate-700 flex items-center gap-1">📍 Branch Address</label>
                  <input
                    type="text"
                    id="branchAddress"
                    placeholder="Auto-filled from IFSC"
                    value={formData.branchAddress}
                    readOnly
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 bg-slate-50`}
                  />
                </div>
                <div className="flex flex-col gap-1" style={isDesktop ? { gridColumn: 'span 2' } : undefined}>
                  <label htmlFor="amount" className="text-sm font-semibold text-slate-700 flex items-center gap-1">₹ Transfer Amount</label>
                  <input
                    type="number"
                    id="amount"
                    placeholder="500.00"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    disabled={merchantPayoutBlacklisted}
                    className={`${inputBase} border-slate-200 ${errors.amount ? inputError : ''}`}
                    step="0.01"
                    min={500}
                  />
                  {errors.amount && <span className="text-red-600 text-sm">{errors.amount}</span>}
                  {!errors.amount && (
                    <span className="text-slate-500 text-xs">
                      Minimum transfer amount: ₹500.00
                    </span>
                  )}
                </div>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || isSubmitting || merchantPayoutBlacklisted}
                  className="flex items-center justify-center gap-2 w-full py-3 px-5 rounded-lg bg-violet-600 text-white font-semibold text-sm border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span>{isSubmitting ? '⏳' : loading ? '⏳' : '💸'}</span>
                  <span>{isSubmitting ? `Submitting in ${countdown}s...` : loading ? 'Processing...' : 'Submit Payout'}</span>
                  {(loading || isSubmitting) && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                </button>
              </div>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm h-fit min-w-0">
            <h3 className="text-lg font-bold text-slate-800 m-0 mb-4">Check Transaction Status</h3>
            <div
              className="flex flex-col gap-2"
              style={isDesktop ? { flexDirection: 'row' } : undefined}
            >
              <input
                type="text"
                placeholder="Enter Merchant Ref or Transaction ID"
                value={statusCheckRef}
                onChange={(e) => setStatusCheckRef(e.target.value)}
                className="flex-1 py-2.5 px-4 border-2 border-slate-200 rounded-lg text-slate-800 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
              />
              <button
                type="button"
                onClick={handleStatusCheck}
                disabled={statusCheckLoading}
                className="py-2.5 px-5 rounded-lg bg-violet-600 text-white text-sm font-semibold border-none cursor-pointer hover:bg-violet-700 disabled:opacity-60"
              >
                {statusCheckLoading ? 'Checking...' : 'Check Status'}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        userName={user?.name}
        userRole={user?.role}
      />

      {/* Wallet Credits Modal */}
      <WalletCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Success!"
        message={modalMessage}
      />

      {/* Error Modal */}
      <SuccessModal
        isOpen={showErrorModal}
        onClose={handleErrorModalClose}
        title={errorModalVariant === 'warning' ? 'Warning' : 'Error'}
        variant={errorModalVariant === 'warning' ? 'warning' : 'default'}
        message={modalMessage}
      />

      {/* Status Result Modal */}
      <TransactionStatusModal
        isOpen={showStatusModal}
        onClose={handleStatusModalClose}
        statusData={statusResult}
        source="viyona"
      />

      {/* Transaction Fees Modal */}
      {transactionFeesData && (
        <TransactionFeesModal
          isOpen={showTransactionFeesModal}
          onClose={handleTransactionFeesCancel}
          onConfirm={handleTransactionFeesConfirm}
          feesData={transactionFeesData}
          beneficiaryName={formData.name}
          beneficiaryAccount={formData.accountNumber}
        />
      )}

      <TpinModal
        isOpen={showTpinModal}
        onClose={() => { setShowTpinModal(false); setTpinError(''); }}
        onSubmit={handleTpinSubmit}
        isLoading={loading}
        error={tpinError}
        maskedMobile={((user as any)?.details?.phone ?? (user as any)?.phone) ? String((user as any)?.details?.phone ?? (user as any)?.phone).slice(-4) : ''}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MerchantNewPayouts;
